/// Dedicated email editor opened from the profile row.
/// Port of mobile/lib/user/ui/change_email_screen.dart.
///
/// Deep links: `/user/email?email=…&code=…` prefill and auto-submit.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import { ApiError } from '@/app/network/apiClient';
import { appRoutes } from '@/app/router/routes';
import { useAuthStore } from '@/auth/handler/authStore';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { PageChrome } from '@/ui/PageChrome';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { PixelOtpField } from '@/ui/pixel/PixelOtpField';

export function ChangeEmailScreen() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const requestChangeEmail = useAuthStore((s) => s.requestChangeEmail);
  const confirmChangeEmail = useAuthStore((s) => s.confirmChangeEmail);
  const isRestoring = useAuthStore((s) => s.isRestoring);

  const deepEmail = (params.get('email') ?? '').trim();
  const deepCode = (params.get('code') ?? '').replace(/\D/g, '').slice(0, 4);

  const [email, setEmail] = useState(deepEmail);
  const [code, setCode] = useState(deepCode.length === 4 ? deepCode : '');
  const [codeSent, setCodeSent] = useState(deepEmail !== '' && deepCode.length === 4);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const deepLinkHandled = useRef(false);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const id = window.setInterval(() => {
      setResendSeconds((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendSeconds]);

  useEffect(() => {
    if (deepLinkHandled.current) return;
    if (!deepEmail || deepCode.length !== 4) return;
    if (user.isAnonymous || isRestoring) return;
    deepLinkHandled.current = true;
    void confirm(deepCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot deep link
  }, [deepEmail, deepCode, user.isAnonymous, isRestoring]);

  if (!isRestoring && user.isAnonymous) {
    return <Navigate to={appRoutes.auth} replace />;
  }

  const mapError = (err: unknown): string => {
    const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
    const lower = message.toLowerCase();
    if (lower.includes('already exists') || lower.includes('taken')) return t('profileEmailTaken');
    if (lower.includes('already your email')) return t('profileEmailUnchanged');
    if (lower.includes('invalid email')) return t('authEmailRequired');
    if (lower.includes('invalid or expired code')) return t('authOtpInvalid');
    if (lower.includes('too many requests')) return t('authOtpResendWait', { seconds: resendSeconds || 60 });
    return message;
  };

  const sendCode = async (): Promise<void> => {
    const next = email.trim();
    setError(null);
    setSuccess(null);
    if (!next) {
      setError(t('authEmailRequired'));
      return;
    }
    if (next.toLowerCase() === user.email.toLowerCase()) {
      setError(t('profileEmailUnchanged'));
      return;
    }
    setSending(true);
    try {
      const resendAfter = await requestChangeEmail(next);
      setCodeSent(true);
      setCode('');
      setSuccess(null);
      setResendSeconds(resendAfter);
    } catch (err) {
      setError(mapError(err));
    } finally {
      setSending(false);
    }
  };

  const confirm = async (otpOverride?: string): Promise<void> => {
    const next = email.trim();
    const otp = (otpOverride ?? code).trim();
    setError(null);
    setSuccess(null);
    if (otp.length !== 4) {
      setError(t('authOtpInvalid'));
      return;
    }
    setConfirming(true);
    try {
      await confirmChangeEmail(next, otp);
      setSuccess(t('profileEmailSaved'));
      window.setTimeout(() => navigate(-1), 450);
    } catch (err) {
      setError(mapError(err));
    } finally {
      setConfirming(false);
    }
  };

  const busy = sending || confirming;

  return (
    <PageChrome title={t('profileChangeEmailTitle')}>
      <div className="flex-1 overflow-y-auto px-6 pb-10 pt-6">
        <label className="mb-2 block leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
          {t('authEmail')}
        </label>
        <PixelContainer
          radius={3}
          pixelScale={2}
          padding="4px 12px"
          backgroundColor={colors.surfaceContainerHighest}
          borderColor={colors.onSurface}
        >
          <input
            autoFocus={!codeSent}
            type="email"
            value={email}
            disabled={busy || codeSent}
            onChange={(event) => {
              setEmail(event.target.value);
              setError(null);
              setSuccess(null);
            }}
            className="w-full bg-transparent outline-none"
            style={{
              fontSize: 22,
              color: colors.onSurface,
              lineHeight: 1.2,
              fontFamily: 'inherit',
              border: 'none',
              padding: '6px 0',
            }}
          />
        </PixelContainer>
        <p className="mt-2 leading-tight" style={{ fontSize: 14, color: colors.onSurface, opacity: 0.65 }}>
          {t('profileChangeEmailHint')}
        </p>
        {error && (
          <p className="mt-2 leading-tight" style={{ fontSize: 16, color: '#ef5350' }}>
            {error}
          </p>
        )}
        {success && (
          <p className="mt-2 leading-tight" style={{ fontSize: 16, color: '#00c853' }}>
            {success}
          </p>
        )}

        {!codeSent ? (
          <PixelContainer
            radius={3}
            pixelScale={2}
            padding="12px 0"
            backgroundColor={colors.onSurface}
            borderColor={colors.onSurface}
            className="mt-3 w-full"
            onClick={busy ? undefined : () => void sendCode()}
          >
            <div className="flex justify-center" style={{ minHeight: 22 }}>
              {sending ? (
                <span
                  className="inline-block h-[18px] w-[18px] animate-spin rounded-full border-2"
                  style={{ borderColor: colors.surface, borderTopColor: 'transparent' }}
                />
              ) : (
                <span className="leading-none" style={{ fontSize: 22, color: colors.surface }}>
                  {t('profileEmailSendCode')}
                </span>
              )}
            </div>
          </PixelContainer>
        ) : (
          <>
            <div className="mt-7 leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
              {t('profileEmailEnterCode')}
            </div>
            <p className="mt-2 leading-tight" style={{ fontSize: 14, color: colors.onSurface, opacity: 0.65 }}>
              {t('profileEmailEnterCodeHint', { email: email.trim() })}
            </p>
            <div className="mt-3">
              <PixelOtpField
                length={4}
                value={code}
                enabled={!busy}
                autoFocus
                color={colors.onSurface}
                surface={colors.surface}
                surfaceContainer={colors.surfaceContainerHighest}
                onChange={(next) => {
                  setCode(next);
                  setError(null);
                  setSuccess(null);
                }}
                onComplete={(next) => void confirm(next)}
              />
            </div>
            <PixelContainer
              radius={3}
              pixelScale={2}
              padding="12px 0"
              backgroundColor={colors.onSurface}
              borderColor={colors.onSurface}
              className="mt-3 w-full"
              onClick={busy ? undefined : () => void confirm()}
            >
              <div className="flex justify-center" style={{ minHeight: 22 }}>
                {confirming ? (
                  <span
                    className="inline-block h-[18px] w-[18px] animate-spin rounded-full border-2"
                    style={{ borderColor: colors.surface, borderTopColor: 'transparent' }}
                  />
                ) : (
                  <span className="leading-none" style={{ fontSize: 22, color: colors.surface }}>
                    {t('authContinue')}
                  </span>
                )}
              </div>
            </PixelContainer>
            <button
              type="button"
              disabled={busy || resendSeconds > 0}
              onClick={() => void sendCode()}
              className="mt-2 w-full bg-transparent p-0 text-center leading-none"
              style={{
                fontSize: 14,
                color: colors.onSurface,
                opacity: busy || resendSeconds > 0 ? 0.4 : 0.7,
                fontFamily: 'inherit',
                border: 'none',
                cursor: busy || resendSeconds > 0 ? 'default' : 'pointer',
              }}
            >
              {resendSeconds > 0
                ? t('authOtpResendWait', { seconds: resendSeconds })
                : t('authOtpResend')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setCodeSent(false);
                setCode('');
                setSuccess(null);
                setError(null);
              }}
              className="mt-2 w-full bg-transparent p-0 text-center leading-none"
              style={{
                fontSize: 14,
                color: colors.onSurface,
                opacity: 0.7,
                fontFamily: 'inherit',
                border: 'none',
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              {t('profileEmailEditAddress')}
            </button>
          </>
        )}
      </div>
    </PageChrome>
  );
}
